import React from 'react';
import CreatableSelect from 'react-select/creatable';
import {components} from 'react-select';
import { useSelector, useDispatch } from "react-redux";

import { addOptions } from "../slices/searchResultsSlice";
import { changeOptionSidePanel, sidePanelOption } from "../slices/optionsSlice";
import { setGoals, resetGoals } from "../slices/goalsSlice";
import { fetchGoals, fetchIntegrations, fetchRecommendations, submitData, createSubmitData } from '../api/searchIntegrations';
import { noOptionsMessage } from "./Constants";
import { MISSING_INTEGRATION_TYPE } from "./Constants";

import { ReactComponent as SearchIcon } from '../assets/search.svg';
import { ReactComponent as PlusIcon } from '../assets/plus.svg';
import { ReactComponent as ArrowIcon } from '../assets/info_arrow.svg';

const defaultSuggestions = [{displayName: "HubSpot"}, {displayName: "Mailchimp"},
{displayName: "Shopify"}, {displayName: "ZenDesk Helpdesk"}, {displayName: "ZenDesk CRM"}, {displayName: "MySQL"}]

const ValueContainerNew = ({children, ...props}) => {
return <components.ValueContainer {...props}> 
    {children}
</components.ValueContainer>
};

const ControlNew = ({children, ...props}) => {
    return <components.Control {...props}>
        <SearchIcon className="search-icon"/>{children}
    </components.Control>
}

const ClearIndicatorNew = () => {
    const selectedIntegrations = useSelector(state => state.searchResult.options) ?? [];
    const selectedGoals = useSelector(state => state.integrationGoals.selectedGoals) ?? [];
    const goals = (useSelector(state => state.integrationGoals.goals) ?? []);
    const allGoals = goals.map(g => {
        return {state: selectedGoals.some(i => i === g.goalId), id: g.goalId}
    });

    const clickHandler = (e) => {
        submitData(createSubmitData(selectedIntegrations, allGoals), (data) => {
            distil.trackEvent('get_started_clicked', ' ', {configurationId: data.userAnonymousId});
            window.location.assign(`https://data.distil.ai/signup?cfid=${data.userAnonymousId}`);
        });
    };
    return ((selectedIntegrations.length > 0 && goals.length > 0) && <div style={{paddingRight: "10px"}}>
        <button
          onTouchStart={clickHandler}
          onClick={clickHandler} className="search-submit">
            <div>Let's get started!</div>
            <ArrowIcon className="search-submit-arrow"/>
        </button>
    </div> || null);
}

const OptionNew = ({children, isFocused, ...props}) => {
    return <components.Option {...props}>
        <div className={`search-option ${isFocused ? "search-option-focused" : ""}`}>
            <div>
                {children}
            </div>
            <div className="search-option-add">
                <PlusIcon/>
            </div>
        </div>
    </components.Option>;
};

const MultiValueNew = ({children, data, onClick, ...props}) => {
    const dispatch = useDispatch();
    const onClickListener = () => dispatch(changeOptionSidePanel(sidePanelOption(true, MISSING_INTEGRATION_TYPE, "Missing Integration")))


    if (data.notExist) {
        return <components.MultiValue data={data} {...props}>
                    <div className="search-not-found-button" onClick={onClickListener} onTouchEnd={onClickListener}>
                        {children}
                    </div>
                </components.MultiValue>
    } else {
        return <components.MultiValue data={data} {...props}>
            {children}
        </components.MultiValue>
    }
}

const PlaceholderNew = ({children, ...props}) => {
    return (!props.isFocused && <components.Placeholder {...props}>
        {children}
    </components.Placeholder> || "");
};

const MultiValueRemoveNew = ({children, innerProps, ...props}) => {
    return <components.MultiValueRemove innerProps={innerProps} {...props}>
        {children}
    </components.MultiValueRemove>;
}

const InputNew = ({children, type, autoComplete, ...props}) => {
    return <components.Input type="text" autoComplete="off" {...props}>
        {children}
    </components.Input>
}

const customComponents = {
    Option: OptionNew,
    Placeholder: PlaceholderNew,
    MultiValueRemove: MultiValueRemoveNew,
    ValueContainer: ValueContainerNew,
    ClearIndicator: ClearIndicatorNew,
    MultiValue: MultiValueNew,
    Control: ControlNew,
    Input: InputNew
}

const customStyles = {
    control: (baseStyle) => ({...baseStyle, borderRadius: "200px", minHeight: "70px", alignItems: "center", border: "1px solid #EAEAEA",
        boxShadow: "0px 12px 24px -4px rgba(0, 0, 0, 0.06)"}),
    input: (baseStyle) => ({...baseStyle, paddingLeft: "0"}),
    indicatorSeparator: () => ({display:  "none"}),
    placeholder: (baseStyle) => ({...baseStyle, fontSize: "140px"}),
    dropdownIndicator: () => ({display: "none"}),
    container: (baseStyle) => ({...baseStyle, margin: "auto auto", width: "10%"}),
    multiValueLabel: (baseStyle, { data }) => {
        if (data.notExist) {
            return {...baseStyle, color: "red", backgroundColor: "transparent", borderRadius: "100px"}
        }
        return {...baseStyle, color: "white", backgroundColor: "#636363", borderRadius: "100px", fontWeight: 'bold'}
    },
    multiValue: (baseStyle, { data }) => {
        if (data.notExist) {
            return {...baseStyle, backgroundColor: "transparent", border: "1px solid red", borderRadius: "100px", padding: "2px 8px"};
        }
        return {...baseStyle, backgroundColor: "#636363", borderRadius: "100px", padding: "2px 8px"}
    },
    multiValueRemove: (baseStyle, { data }) => {
        if (data.notExist) {
            return {...baseStyle, color: "red", ":hover":{backgroundColor:"transparent", color: "red"}, cursor:"pointer"};
        }
        return {...baseStyle, color: "white", ":hover":{backgroundColor:"transparent", color: "white"}, cursor:"pointer"}
    },
    noOptionsMessage: (baseStyle, {selectProps}) => {
        if (!selectProps.inputValue) {
            return {...baseStyle, display:"none"}
        }
        return baseStyle
    },
    menu: (baseStyle) => ({...baseStyle, marginTop: 0, marginBottom: 0, borderRadius: 20, top: "110%", overflow: "hidden", padding: "20px 0"}),
    menuList: (baseStyle) => ({...baseStyle, paddingTop: 0, paddingBottom: 0, overflow: "hidden"}),
    option: (baseStyle) => ({...baseStyle, ":active": {backgroundColor: "#FCFCFC", borderRadius: "20px"}, color: "#2E2E2E", textAlign: "start",
        backgroundColor: "transparent", overflow: "hidden", padding: "0px 0px",
        ":hover": {backgroundColor: "#FCFCFC", borderBottom: "1px solid #EAEAEA", borderTop: "1px solid #EAEAEA", color: "black", marginBottom: "-1px", marginTop: "-1px"}}),
    valueContainer: (baseStyle) => ({...baseStyle, padding: "2px 0px", display: "flex"}),
};

const SearchSelect = ({ placeholder }) => {
    const [isLoading, setLoading] = React.useState(false);
    const [options, setOptions] = React.useState([]);
    const [value, setValue] = React.useState([]);
    const [suggestArr, setSuggestArr] = React.useState([]);
    const [defaultSuggestedArr, setDefaultSuggestedArr] = React.useState([]);
    const [inputValue, setInputValue] = React.useState("");
    const [showMenu, setShowMenu] = React.useState(false);

    const optionsStore = useSelector(state => state.searchResult.options);
    const dispatch = useDispatch();

    React.useEffect(() => {
        if (optionsStore.length > 0) {
            const array = []
            if( defaultSuggestedArr.length === 0 ){
                defaultSuggestions.map((item) => { 
                    fetchIntegrations(item.displayName,  (data) => {
                        if (data != null ) {
                            const filteredSelected = {
                                value: item.displayName,
                                label: item.displayName,
                                id: data[0].integrationId,
                                single: data.length === 1,
                            };
                            array.push(filteredSelected)
                            setDefaultSuggestedArr(currentArray => [...currentArray, filteredSelected]);           
                        }
                    })
                })
            }

            fetchRecommendations(optionsStore.map(i => i.id), (data) => {
                if (data != null && data.length > 0) {
                    setSuggestArr(data.map(o => ({
                        value: o.displayName,
                        label: o.displayName,
                        id: o.integrationId
                    } ) ) ) ;
                } else {
                    setSuggestArr(array);
                }
            });
        } else{
            defaultSuggestions.map((item) => { 
                fetchIntegrations(item.displayName,  (data) => {
                    if (data != null ) {
                        const filteredSelected = {
                            value: item.displayName,
                            label: item.displayName,
                            id: data[0].integrationId,
                            single: data.length === 1,
                        };
                        setSuggestArr(currentArray => [...currentArray, filteredSelected]);
                        setDefaultSuggestedArr(currentArray => [...currentArray, filteredSelected]);
                    }
                })
            })
        }
        setValue(optionsStore);
    }, []);

    customStyles.control = React.useCallback((baseStyle) => {
        if (window.screen.availWidth >= 915) {
            return {...baseStyle, borderRadius: "200px", minHeight: "70px", alignItems: "center", border: "1px solid #EAEAEA",
            boxShadow: "0px 12px 24px -4px rgba(0, 0, 0, 0.06)", width: "100%", margin: "0 auto"};
        }
        return {...baseStyle, borderRadius: "200px", minHeight: "50px", alignItems: "center", border: "1px solid #EAEAEA",
        boxShadow: "0px 12px 24px -4px rgba(0, 0, 0, 0.06)", width: "100%"};
    }, [])

    customStyles.placeholder = React.useCallback((baseStyle) => {
        if (window.screen.availWidth >= 915) {
            return {...baseStyle}
        }
        return {...baseStyle, fontSize: "80%"}
    }, [])

    customStyles.container = React.useCallback((baseStyle) => {
        if (window.screen.availWidth >= 915) {
            return {...baseStyle, margin: "0 auto", maxWidth: "870px"}
        }
        return {...baseStyle, margin: "0 auto", width: "100%"}
    }, [])

    const onInputChange = (searchTerm) => {
        if (searchTerm.length > 2) {
            fetchIntegrations(searchTerm, (data) => {
                distil.trackEvent('integration_search', ' ', {searchTerm, searchResults: data});

                if(data == null || data.length === 0) {
                    distil.trackEvent('integration_not_found', ' ', {searchTerm});
                }
                
                if (data != null) {
                    setOptions(data.map(o => ({
                        value: o.displayName,
                        label: o.displayName,
                        id: o.integrationId
                    })));
                }
            })
            setShowMenu(true);
        } else {
            setShowMenu(false);
        }
        setInputValue(searchTerm);
    };

    const onChange = (selected, action, source = 'search') => {

        if(action.action === 'remove-value') {
            if(action.removedValue.id && action.removedValue.id!=='') {
                distil.trackEvent('integration_removed', ' ', {integrationId: action.removedValue.id})
            }
        } else if (action.action === 'select-option') {
            distil.trackEvent('integration_selected', ' ', {integrationId: action.option.id, source})
        }
        
        if(selected.length > 0 && selected[0].single === false){
            onInputChange(selected[0].label);
            return;
        }
        
        setValue(selected);
        const filteredSelected = selected.filter(o => !o.notExist);
        const filteredSelectedIds = filteredSelected.map(i => i.id);
        dispatch(addOptions(filteredSelected));
        if (filteredSelected.length < 1) {
            dispatch(resetGoals());
            setSuggestArr(defaultSuggestedArr);
            return;
        }

        fetchRecommendations(filteredSelectedIds, (data) => {
            if (data != null && data.length > 0) {
                setSuggestArr(data.map(o => ({
                    value: o.displayName,
                    label: o.displayName,
                    id: o.integrationId
                } ) ) ) ;
            } else {
                const defaultSuggestionsWithoutSelected = defaultSuggestedArr.filter(i => !selected.some(s => s.label === i.label))
                setSuggestArr(defaultSuggestionsWithoutSelected);
            }
        });

        fetchGoals(filteredSelectedIds, (data) => dispatch(setGoals(data ?? [])));
    };

    const integrationNotFoundLabel = (inputString) => {
        return <div className="search-label-not-found">
            {inputString} not found
            <div>
                Tell us more <ArrowIcon className="search-label-not-found-arrow"/>
            </div>
        </div>
    };

    const isValidIntegrationNotFound = (inputValue, value, options) => {
        if (inputValue.length > 2 && options.length < 1) {
            return true;
        }
    };

    const onCreateIntegrationNotFoundOption = (inputString) => {
        dispatch(changeOptionSidePanel(sidePanelOption(true, MISSING_INTEGRATION_TYPE, "Missing Integration")))
        setValue([...value, {label: inputString, value: inputString, notExist: true}]);
    };
   
    return (
        <React.Fragment>
            <CreatableSelect 
            autoFocus={true}
            formatCreateLabel={integrationNotFoundLabel}
            isValidNewOption={isValidIntegrationNotFound}
            onCreateOption={onCreateIntegrationNotFoundOption}
            hideSelectedOptions={true}
            isMulti={true}
            value={value}
            styles={customStyles}
            isLoading={isLoading}
            placeholder={placeholder}
            options={options}
            menuIsOpen={showMenu}
            components={customComponents}
            onInputChange={onInputChange}
            onChange={onChange}
            inputValue={inputValue}
            noOptionsMessage={() => (noOptionsMessage)}
            />
            {suggestArr && suggestArr.length > 0 &&
            <div className="search-suggestions">
                <div>Suggested</div>
                {suggestArr.map(v => <div key={v.id} className="search-suggestions-item" onClick={() => {
                    onChange([...value, v], {action: 'select-option', option: v}, 'recommendation');
                }}>
                    {v.label}
                </div>)}
            </div>}
        </React.Fragment>
    )
}

export default SearchSelect;
